/**
 * shadcn/Tailwind replacement for ContentDocumentBreadcrumb.
 * Renders a clickable breadcrumb trail for navigating between Content documents.
 */

import React, { useEffect, useMemo } from 'react';
import { FileText, ChevronRight } from 'lucide-react';
import { resolveEntityViewHash } from '../../utils/entityViewNavigationUtils';

const TRAIL_KEY = 'hades.contentDocTrail.v1';
const MAX_TRAIL = 10;

interface TrailEntry {
    id: string;
    displayName: string;
}

function getTrail(): TrailEntry[] {
    try {
        const raw = window.sessionStorage.getItem(TRAIL_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function setTrail(trail: TrailEntry[]): void {
    try {
        window.sessionStorage.setItem(TRAIL_KEY, JSON.stringify(trail.slice(-MAX_TRAIL)));
    } catch { /* ignore */ }
}

function recordVisit(id: string, displayName: string): void {
    const trail = getTrail();
    const existingIndex = trail.findIndex((e) => e.id === id);
    if (existingIndex >= 0) {
        trail.length = existingIndex + 1;
        trail[existingIndex].displayName = displayName;
    } else {
        trail.push({ id, displayName });
    }
    setTrail(trail);
}

export function clearContentDocumentTrail(): void {
    try {
        window.sessionStorage.removeItem(TRAIL_KEY);
    } catch { /* ignore */ }
}

export interface ShadcnContentBreadcrumbProps {
    currentEntityId: string;
    currentDisplayName: string;
}

export const ShadcnContentBreadcrumb: React.FC<ShadcnContentBreadcrumbProps> = ({
    currentEntityId,
    currentDisplayName,
}) => {
    useEffect(() => {
        if (currentEntityId) {
            recordVisit(currentEntityId, currentDisplayName || currentEntityId);
        }
    }, [currentEntityId, currentDisplayName]);

    const trail = useMemo(() => getTrail(), [currentEntityId, currentDisplayName]);

    if (trail.length < 2) return null;

    const handleNavigate = async (entry: TrailEntry) => {
        if (entry.id === currentEntityId) return;
        const hash = await resolveEntityViewHash('content', entry.id);
        window.location.hash = hash.startsWith('#') ? hash.substring(1) : hash;
    };

    return (
        <div className="flex items-center gap-1 mb-2 overflow-x-auto scrollbar-thin min-h-[28px]">
            <FileText size={14} className="shrink-0 text-muted-foreground/50" />
            {trail.map((entry, index) => {
                const isCurrent = entry.id === currentEntityId;
                return (
                    <React.Fragment key={entry.id}>
                        {index > 0 && (
                            <ChevronRight size={12} className="shrink-0 text-muted-foreground/35" />
                        )}
                        {isCurrent ? (
                            <span className="text-xs font-semibold text-foreground whitespace-nowrap">
                                {entry.displayName}
                            </span>
                        ) : (
                            <button
                                onClick={() => handleNavigate(entry)}
                                className="text-xs text-primary underline underline-offset-2 whitespace-nowrap hover:text-primary/80 transition-colors cursor-pointer bg-transparent border-none p-0"
                            >
                                {entry.displayName}
                            </button>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};
